import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { appsApi } from "../../api/apps.api";
import { usersApi } from "../../api/users.api";

interface Props {
  appId: string;
}

export function AppUserAccess({ appId }: Props) {
  const qc = useQueryClient();

  const appUsersQ = useQuery({
    queryKey: ["apps", appId, "users"],
    queryFn: () => appsApi.listUsers(appId),
  });

  const allUsersQ = useQuery({
    queryKey: ["users"],
    queryFn: () => usersApi.list(),
  });

  const setUsers = useMutation({
    mutationFn: (userIds: string[]) => appsApi.setUsers(appId, userIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "users"] });
    },
  });

  const [grantedUserIds, setGrantedUserIds] = useState<Set<string>>(new Set());

  // Match the Users page: only active (non-disabled) users are listed or
  // granted. Disabled users are dropped, so saving also revokes their access.
  const allUsers = useMemo(
    () => (allUsersQ.data ?? []).filter((u) => !u.disabled),
    [allUsersQ.data],
  );
  const activeIds = useMemo(() => new Set(allUsers.map((u) => u.id)), [allUsers]);

  useEffect(() => {
    if (appUsersQ.data) {
      setGrantedUserIds(
        new Set(appUsersQ.data.map((u) => u.id).filter((id) => activeIds.has(id))),
      );
    }
  }, [appUsersQ.data, activeIds]);

  const accessChanged = useMemo(() => {
    if (!appUsersQ.data) return false;
    const current = new Set(appUsersQ.data.map((u) => u.id).filter((id) => activeIds.has(id)));
    if (current.size !== grantedUserIds.size) return true;
    for (const id of grantedUserIds) if (!current.has(id)) return true;
    return false;
  }, [appUsersQ.data, grantedUserIds, activeIds]);

  const allSelected = allUsers.length > 0 && allUsers.every((u) => grantedUserIds.has(u.id));

  function toggleUser(userId: string) {
    setGrantedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) {
      setGrantedUserIds(new Set());
    } else {
      setGrantedUserIds(new Set(allUsers.map((u) => u.id)));
    }
  }

  function saveAccess() {
    setUsers.mutate(Array.from(grantedUserIds));
  }

  if (appUsersQ.isLoading || allUsersQ.isLoading) {
    return (
      <div className="card" style={{ marginTop: 16 }}>
        <p className="muted">Loading users…</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>User access</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        Choose which users can see this app on their dashboard.
      </p>

      {allUsers.length === 0 ? (
        <p className="muted">No users registered yet.</p>
      ) : (
        <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={allSelected}
              onChange={toggleAll}
            />
            <span>All users</span>
          </label>
          <div
            style={{
              borderTop: "1px solid var(--border)",
              marginTop: 4,
              paddingTop: 8,
              display: "grid",
              gap: 6,
            }}
          >
            {allUsers.map((user) => (
              <label key={user.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={grantedUserIds.has(user.id)}
                  onChange={() => toggleUser(user.id)}
                />
                <span>{user.displayName ?? user.email}</span>
                {user.displayName && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    {user.email}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}

      <button
        className="primary"
        onClick={saveAccess}
        disabled={!accessChanged || setUsers.isPending}
      >
        {setUsers.isPending ? "Saving…" : "Save user access"}
      </button>
      {setUsers.isError && (
        <p className="error-text" style={{ marginTop: 8 }}>
          Failed to save user access
        </p>
      )}
      {setUsers.isSuccess && !accessChanged && (
        <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>
          Saved
        </span>
      )}
    </div>
  );
}
